import {useContext, useState} from 'react'
import {useParams} from 'react-router-dom'
import {format} from 'date-fns'
import {find, keys, lowerCase, map, startCase} from 'lodash'
import {useIsFetching, useIsMutating, useMutation, useQuery, useQueryClient,} from '@tanstack/react-query'
import {useOktaAuth} from '@okta/okta-react'

import {
  ActivityState,
  Address,
  Customer,
  CustomerInfo,
  QualificationTier,
  Score,
  Subscription,
} from '@nxcr-org/web-api-interface/lib/domain_pb'
import reservation, {Program,} from '@nxcr-org/web-api-interface/lib/subscription_pb'
import {Reservation} from '@nxcr-org/web-api-interface/lib/billing_service_pb'

import {Vehicle} from '@nxcr-org/web-api-interface/lib/fleet_service_pb'

import {NexusService} from 'global-apis/nexus-service'
import {FleetService} from '../../api/fleet-service'
import {ReservationService} from '../../api/reservation-service'
import {SubscriptionService} from '../../api/subscription-service'
import {getIsAdmin} from '../../../../utils/getIsAdmin'
import {safeParseJSON} from '../../../../utils/safeParseJSON'
import {getOrderStatus} from '../../api/reservation-service.hooks'
import {CQEService} from '../../../global-APIs/nexus-service-cqe'
import {Snack, SnackbarContext} from '../../../shared/Snackbar'
import {GetTelematicsResponse} from '@nxcr-org/web-api-interface/lib/telematics_service_pb'
import {ClientContext} from '../../../../utils/clientContext'
import {GetCustomerNotesResponse} from '@nxcr-org/web-api-interface/lib/nexus_service_pb'

export function useReservation() {
  const queryClient = useQueryClient()
  const { reservationId } = useParams<{ reservationId: string }>()
  const { authState } = useOktaAuth()
  const [currentNote, setCurrentNote] = useState('')
  const [changeProgramOpen, setChangeProgramOpen] = useState(false)
  const [overrideDisqualificationVendor, setOverrideDisqualificationVendor] =
    useState(undefined)
  const userGroups = authState?.idToken?.claims?.groups
  const isAdmin = getIsAdmin(userGroups)
  const { clientId, setClientId } = useContext(ClientContext)

  const { setSnack } = useContext(SnackbarContext)

  const toggleChangeProgram = () => {
    setChangeProgramOpen((currentState) => !currentState)
  }

  const {
    data: reservation,
    isSuccess: reservationSuccess,
    error: reservationError,
  } = useQuery({
    queryKey: ['/reservation', reservationId],
    queryFn: () => {
      return NexusService.getProgramReservation(reservationId).then(
        handleReservationResponse
      )
    },
      placeholderData: getPlaceholderReservation(),
    }
  )
  const handleReservationResponse = async (res: Reservation.AsObject) => {
    const subscriptionsList = (
      await NexusService.listCustomerSubscriptions(res.customerId)
    ).subscriptionsList

    const { customer } = await NexusService.getCustomerById(res.customerId)

    const activeSubscription = find(subscriptionsList, {
      status: 'ACTIVE',
    })

    const activeOrFirstSubscription = activeSubscription
      ? activeSubscription
      : subscriptionsList[0]

    const vehicleId = activeOrFirstSubscription.vehicleId
    const assignedVehicle = vehicleId
      ? await getAssignedVehicle(vehicleId)
      : undefined

    const customerInfo = formatCustomerInfo({ customer, reservation: res })
    const isQualified = getIsQualified(customerInfo.qualificationTier)
    if (clientId !== customer.clientId) {
      setClientId(customer.clientId)
    }
    return {
      ...res,
      customer,
      customerInfo,
      isQualified,
      reservationStatus: getOrderStatus(
        res?.status,
        customer?.customerInfo?.qualificationTier
      ),
      reservation: formatReservation(res),
      vehicle: getReservationVehicle(res, assignedVehicle, telematics),
      isCancelled: getIsCancelled(res),
      qualifications: {
        scores: getQualification(customer?.customerInfo?.scoresList),
        hasQualifications: customer?.customerInfo?.scoresList?.length > 0,
      },
      assignedVehicle: assignedVehicle,
      subscriptionId: activeOrFirstSubscription.id,
    }
  }

  const { data: telematics } = useQuery({
    queryKey: ['telematics', reservation?.assignedVehicle?.id],
    queryFn: () => {
      return FleetService.getTelematics(reservation?.assignedVehicle?.id).catch((err) => {
        console.error('getTelematics Error', err)
        return {
          telematics: [],
        }
      })
    },
    enabled: !!reservation?.assignedVehicle?.id,
  }
  )

  const { data: mostRecentNote } = useQuery({
    queryKey: ['notes', reservation?.customer?.id],
    queryFn: async () => {
      let notesResponse: GetCustomerNotesResponse.AsObject
      try {
        notesResponse = await CQEService.getCustomerNotes({
          customerId: reservation?.customer?.id
        })
      } catch (err) {
        console.error('CQE error', err)
        notesResponse.notesList = [{noteText: 'CQE SERVICE ERROR ON NOTE FETCH',
          customerId: '',
          id: '',
          timestamp: 0,
          authorEmail: ''}]
      }
      const {notesList} = notesResponse
      const lastestNote = notesList[0]?.noteText || ''
      setCurrentNote(lastestNote)
      return lastestNote
    },
    enabled: reservationSuccess && !!reservation?.customer?.id,
  }
  )

  const { data: cqeScores } = useQuery({
    queryKey: ['scores', reservation?.customer?.id],
    queryFn: async () => {
      try {
        return await CQEService.getCustomerCQEScores({
          customerId: reservation?.customer?.id
        })
      } catch (err) {
        console.error('getCQEScoresError', err)
        return {
          scoresList: []
        }
      }
    },
    enabled: !!reservation?.customer?.id,
  }
  )

  const {
    mutateAsync: overrideQualification,
    isSuccess: overrideQualificationSuccess,
  } = useMutation({
    mutationFn: (result: ProcessCustomerArgs) => {
      if (result.decision === ProcessOptions.approved) {
        return CQEService.overrideCustomerQualification({
          customerId: reservation.customer.id,
          tier: QualificationTier.TIER_3_MANUAL,
          disqualifiedScoreId: '',
        })
      }

      if (!overrideDisqualificationVendor) {
        throw new Error('No score set for disqualification')
      }

      return CQEService.overrideCustomerQualification({
        customerId: reservation.customer.id,
        tier: QualificationTier.DISQUALIFIED_MANUAL,
        disqualifiedScoreId: overrideDisqualificationVendor.id,
      })
    },
      onSuccess() {
        queryClient.invalidateQueries({queryKey: ['/reservation', reservationId]})
      },
    }
  )

  const { mutate: assignVehicle } = useMutation({
    mutationFn: (vehicleId: string) => {
      return SubscriptionService.addVehicleAssignment({
        vehicleId,
        subscriptionId: reservation.subscriptionId,
      })
    },
      async onSuccess() {
        await queryClient.refetchQueries({queryKey: ['/reservation', reservationId]})
        await queryClient.invalidateQueries({queryKey: ['getVehicles']})
      },
      onError(error: Error) {
        setSnack(
          new Snack({
            message: `Error assigning vehicle: \n\n${error?.message}`,
            color: 'error',
            open: true,
          })
        )
      },
    }
  )

  const { mutate: unassignVehicle } = useMutation({
    mutationFn: () => {
      return SubscriptionService.removeVehicleAssignment({
        subscriptionId: reservation.subscriptionId,
      })
    },
      async onSuccess() {
        await queryClient.refetchQueries({queryKey: ['/reservation', reservationId]})
        await queryClient.invalidateQueries({queryKey: ['getVehicles']})
      },
      onError(error: Error) {
        setSnack(
          new Snack({
            message: `Error unassigning vehicle: \n\n${error?.message}`,
            color: 'error',
            open: true,
          })
        )
      },
    }
  )

  const {
    mutate: recalculateCustomerScores,
    error: cqeError,
    isSuccess: recalculateCustomerScoresSuccess,
  } = useMutation({
    mutationFn: () => {
      return CQEService.recalculateCustomerQualification({
        customerId: reservation?.customer?.id,
        scoreNamesList: [0, 1, 2, 3],
        skipQualification: false,
      })
    },
      onSuccess() {
        queryClient.invalidateQueries({queryKey: ['/reservation', reservationId]})
      },
    }
  )

  const { mutate: addNote } = useMutation({
    mutationFn: () => {
      const data = {
        customerId: reservation.customer.id,
        noteText: currentNote,
      }

      return CQEService.addCustomerNote(data)
    },
      onSuccess() {
        queryClient.invalidateQueries({queryKey: ['/reservation', reservationId]})
        queryClient.invalidateQueries({queryKey: ['notes', reservation?.customer?.id]})
      },
    }
  )

  const { mutate: cancelReservation, error: cancelError } = useMutation({
    mutationFn: async ({
                         actionReason,
                         userType,
                       }: {
      actionReason: any
      userType: Subscription.SubscriptionAction.UserType
    }) => {
      try {
        return await ReservationService.cancelReservation({
          actionReason,
          userType,
          subscriptionId: reservation.reservation.id,
          customerId: reservation.customer.id,
          reservationId: reservation.reservation.id,
          customerReason: ''
        })
      } catch (err) {
        console.error({
          message: 'Error cancelling subscription',
          err,
          params: {
            actionReason,
            userType
          }
        })

        // ! Allow UI to handle but log error so that datadog picks up additional data
        throw err
      }
    },
      onSuccess() {
        queryClient.invalidateQueries({queryKey: ['/reservation', reservationId]})
      },
    }
  )

  const setReload = () => {
    queryClient.invalidateQueries({queryKey: ['/reservation', reservationId]})
  }

  const isFetching = useIsFetching({queryKey: ['/reservation']})
  const isMutating = useIsMutating()

  return {
    authState,
    reservation,
    addNote,
    mostRecentNote,
    telematics,
    assignVehicle,
    unassignVehicle,
    overrideQualification,
    overrideQualificationSuccess,
    recalculateCustomerScores,
    recalculateCustomerScoresSuccess,
    cancelReservation,
    isAdmin,
    currentNote,
    setCurrentNote,
    setReload,
    reservationError,
    cqeError,
    cqeScores: cqeScores?.scoresList,
    cancelError,
    changeProgramOpen,
    toggleChangeProgram,
    isLoading: isFetching > 0 || isMutating > 0,
    disqualificationVendor: getDisqualificationVendor(cqeScores?.scoresList),
    overrideDisqualificationVendor,
    setOverrideDisqualificationVendor,
  }
}

export function getIsQualified(
  qualificationTier: QualificationTier
) {
  return (
    qualificationTier === QualificationTier.TIER_1 ||
    qualificationTier === QualificationTier.TIER_2 ||
    qualificationTier === QualificationTier.TIER_3 ||
    qualificationTier === QualificationTier.TIER_1_MANUAL ||
    qualificationTier === QualificationTier.TIER_2_MANUAL ||
    qualificationTier === QualificationTier.TIER_3_MANUAL
  )
}

function getPlaceholderReservation() {
  return {
    customer: {
      subscriptionsList: [{}],
    },
    customerInfo: {},
    isQualified: false,
    reservationStatus: '',
    reservation: {
      customer: {
        subscriptionsList: [{}],
      },
    },
    vehicle: {},
    isCancelled: false,
    qualifications: {
      scores: [],
      hasQualifications: false,
    },
    payments: {
      monthly: 0,
      securityDeposit: 0,
      start: 0,
    },
    assignedVehicle: {},
  }
}

async function getAssignedVehicle(
  vehicleId: string
): Promise<Vehicle.AsObject | undefined> {
  return FleetService.getVehicleById(vehicleId)
    .then((res) => {
      return res.vehicle as Vehicle.AsObject
    })
    .catch((err) => {
      console.error({
        errorOnGetAssignedVehicle: err,
      })
      return undefined
    })
}

export function getReservationVehicle(
  reservation: Reservation.AsObject,
  vehicle?: Vehicle.AsObject,
  mileage?: GetTelematicsResponse.AsObject
): VehicleBlockProps {
  const colorPreference =
    reservation?.color === 'any' ? 'No Preference' : reservation?.color

  return {
    make: vehicle?.make || reservation?.make,
    model: vehicle?.model || reservation?.model,
    colorPreference,
    mileage: mileage?.telematicsList?.[0]?.odometer.reading.toString() || 'N/A',
    vin: vehicle?.vin || 'N/A',
    startFee: reservation?.startFee,
    monthly: reservation?.monthlyRent,
    deposit: reservation?.deposit,
    trim: vehicle?.trim || 'N/A',
  }
}

export function getIsCancelled(reservationObj: Reservation.AsObject) {
  return (
    reservationObj.status ===
    reservation.ReservationStatus.REFUND_REQUESTED.toString()
  )
}

export function getQualification(
  scores: Score.AsObject[]
): FormattedQualification[] {
  return map(scores, (score) => {
    const timeIsInNano =
      score.scoreName === 'liquidity' || score.scoreName === 'fico'

    const qualificationTime = timeIsInNano
      ? new Date(Math.round(score.timestamp / 1000000))
      : new Date(score.timestamp)

    return {
      qualificationTime: format(qualificationTime, 'hh:mm aa'),
      qualificationDate: format(qualificationTime, 'MM/dd/yyyy'),
      result: score.isPassing ? 'Accepted' : 'Rejected',
      reason: 'TBD',
      score: score.scoreValue,
      tier: 'TBD',
      vendor: score.vendorName,
      sourceData: [{ ...score, sourceData: safeParseJSON(score.sourceData) }],
      isDisqualifiedScore: score.isDisqualifiedScore,
    }
  })
}

const getAddressFromAddressList = (addressesList, addressType) =>
  addressesList.find((address) => address.addressType == addressType)

export function formatCustomerInfo({
  customer,
  reservation,
}: {
  customer: Customer.AsObject
  reservation: Reservation.AsObject
}): FormattedCustomerInfo {
  return {
    ...customer?.customerInfo,
    accountNumber: customer?.accountNumber,
    reservationNumber: reservation?.reservationNumber,
    fullName: `${customer?.customerInfo?.firstName} ${customer?.customerInfo?.lastName}`,
    zipcode: findZipcode(customer?.customerInfo?.addressesList),
    dealer: 'Autonomy',
    hasVehicleAssigned: isCustomerVehicleAssigned(customer),
    garagingAddress: getAddressFromAddressList(
      customer?.customerInfo?.addressesList,
      Address.AddressType.PARKING
    ),
    licenseAddress: getAddressFromAddressList(
      customer?.customerInfo?.addressesList,
      Address.AddressType.LICENSE
    ),
    contactAddress: getAddressFromAddressList(
      customer?.customerInfo?.addressesList,
      Address.AddressType.CONTACT
    ),
    //  @TODO: Should be currentActivityState
    currentActivity: getCurrentActivity(customer?.activityState),
  }
}

export function isCustomerVehicleAssigned(customer: Customer.AsObject) {
  const subscriptionsList = customer.subscriptionsList || []

  if (subscriptionsList.length === 0) {
    return false
  }

  if (subscriptionsList.length === 1) {
    return subscriptionsList[0].vehicleId?.length > 0
  }

  const assignedVehicle = find(subscriptionsList, (subscription) => {
    const subscriptionIsntClosed =
      subscription.status !== Subscription.Status.CLOSED
    return subscription.vehicleId?.length > 0 && subscriptionIsntClosed
  })

  return !!assignedVehicle
}

export function findZipcode(
  addresses: Customer.AsObject['customerInfo']['addressesList']
) {
  if (addresses.length === 0) {
    return 'N/A'
  }

  return addresses[0].postal
}

export function formatReservation(
  reservation: Reservation.AsObject
): FormattedReservation {
  return {
    ...reservation,
    formattedCreated: format(new Date(reservation.created), 'MM/dd/yy HH:MM'),
  }
}

export function getFormattedActivities() {
  return map(keys(ActivityState), (activity) => startCase(lowerCase(activity)))
}

export function getCurrentActivity(
  customerActivityState: Customer.AsObject['activityState']
) {
  const formattedActivities = getFormattedActivities()
  return formattedActivities[customerActivityState]
}

export function getDisqualificationVendor(
  cqeScores: Score.AsObject[]
): Score.AsObject {
  return cqeScores?.find((score) => score.isDisqualifiedScore)
}

export interface VehicleBlockProps {
  make: string
  model: string
  colorPreference: string
  vin: string
  mileage: string
  dateListed?: number
  monthly: number
  startFee: number
  deposit: number
  trim: string
  termLength?: number
}

export interface FormattedReservationResponse {
  reservation?: FormattedReservation
  customerInfo?: FormattedCustomerInfo
  customer?: Customer.AsObject
  program?: Program.AsObject
  vehicle?: VehicleBlockProps
  qualifications?: QualificationBlockProps
  isCancelled: boolean
  reservationStatus: string
  payments?: {
    monthly: number
    securityDeposit: number
    start: number
  }
  assignedVehicle?: Vehicle.AsObject
  isQualified?: boolean
  programId?: string
}

export type FormattedReservation = Reservation.AsObject & {
  formattedCreated: string
}

export type FormattedCustomerInfo = CustomerInfo.AsObject & {
  fullName: string
  accountNumber: string
  reservationNumber: string
  zipcode: string
  dealer: string
  hasVehicleAssigned: boolean
  currentActivity: string
  garagingAddress: Address.AsObject
  licenseAddress: Address.AsObject
  contactAddress: Address.AsObject
  // currentStatus: string
}

export interface FormattedQualification {
  qualificationTime: string
  qualificationDate: string
  result: string
  reason: string
  score: string
  tier: string
  vendor: string
  sourceData: any
  isDisqualifiedScore: boolean
}

export type QualificationBlockProps = {
  scores: FormattedQualification[]
}

export enum ProcessOptions {
  unchanged,
  rejected,
  approved,
  recalculate,
}

export type ProcessCustomerArgs = {
  decision: ProcessOptions
  qualificationTier?: QualificationTier
  note: string
}
